// src/components/investments/InvestmentCard.jsx
// Beautified Professional Design with Modern UI
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const InvestmentCard = ({ item, type }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Helper functions
  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    const num = Number(amount);
    if (num >= 10000000) return `₹${(num/10000000).toFixed(1)}Cr`;
    if (num >= 100000) return `₹${(num/100000).toFixed(1)}L`;
    if (num >= 1000) return `₹${(num/1000).toFixed(0)}K`;
    return `₹${num}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num/1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num/1000).toFixed(0)}K`;
    return num;
  };

  // Calculate metrics
  const fundedPercent = item.fundingGoal 
    ? Math.round((Number(item.currentFunding || 0) / Number(item.fundingGoal)) * 100) 
    : 0;
  
  const daysLeft = item.fundingDeadline 
    ? Math.max(0, Math.ceil((new Date(item.fundingDeadline) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  // Routing
  const detailsRoute = type === 'creator' ? `/creator/${item.id}` : `/project/${item.id}`;
  
  const handleViewDetails = (e) => {
    e.stopPropagation();
    const urlParams = new URLSearchParams(location.search);
    const ref = urlParams.get('ref');
    navigate(ref ? `${detailsRoute}?ref=${ref}` : detailsRoute);
    
    if (window.gtag) {
      window.gtag('event', 'view_investment_details', {
        item_id: item.id,
        item_type: type,
        referral_code: ref || 'direct'
      });
    }
  };

  const handleInvest = (e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    
    if (token) {
      navigate(detailsRoute);
    } else {
      navigate(`/login?returnUrl=${encodeURIComponent(detailsRoute)}`);
    }

    if (window.gtag) {
      window.gtag('event', 'click_invest_button', {
        item_id: item.id,
        item_type: type,
        user_logged_in: !!token
      });
    }
  };

  // Get gradient colors based on type
  const getGradientColors = () => {
    switch(type) {
      case 'creator': return 'from-purple-500 via-pink-500 to-red-500';
      case 'funding': return 'from-blue-500 via-cyan-500 to-teal-500';
      case 'network': return 'from-green-500 via-emerald-500 to-teal-500';
      case 'external': return 'from-indigo-500 via-purple-500 to-pink-500';
      default: return 'from-blue-500 via-purple-500 to-pink-500';
    }
  };

  // Get icon based on type
  const getIcon = () => {
    switch(type) {
      case 'creator': return '👤';
      case 'funding': return '🎬';
      case 'network': return '📺';
      case 'external': return '🏢';
      default: return '💼';
    }
  };

  // Render badges based on type
  const renderBadges = () => {
    const badges = [];
    
    if (type === 'creator') {
      badges.push(
        <span key="creator" className="inline-flex items-center gap-1 text-xs font-bold text-purple-700 bg-gradient-to-r from-purple-100 to-pink-100 px-3 py-1.5 rounded-full border border-purple-200">
          👤 CREATOR
        </span>
      );
      if (item.category) badges.push(
        <span key="cat" className="inline-flex items-center text-xs font-semibold text-indigo-700 bg-indigo-50 px-3 py-1.5 rounded-full border border-indigo-200">
          {item.category}
        </span>
      );
      if (item.isFeatured) badges.push(
        <span key="feat" className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-gradient-to-r from-yellow-100 to-amber-100 px-3 py-1.5 rounded-full border border-amber-300 animate-pulse">
          ⭐ FEATURED
        </span>
      );
    } else if (type === 'funding') {
      badges.push(
        <span key="type" className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 bg-gradient-to-r from-blue-100 to-cyan-100 px-3 py-1.5 rounded-full border border-blue-200">
          🎬 {item.contentType || 'PROJECT'}
        </span>
      );
      badges.push(
        <span key="fund" className="inline-flex items-center text-xs font-bold text-orange-700 bg-gradient-to-r from-orange-100 to-red-100 px-3 py-1.5 rounded-full border border-orange-200">
          💰 FUNDING
        </span>
      );
    } else if (type === 'network') {
      const roi = item.expectedROI || item.expectedReturn;
      badges.push(
        <span key="net" className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-gradient-to-r from-green-100 to-emerald-100 px-3 py-1.5 rounded-full border border-green-200">
          📺 NETWORK
        </span>
      );
      badges.push(
        <span key="mcn" className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200">
          ✓ MCN OWNED
        </span>
      );
      if (roi) badges.push(
        <span key="roi" className="inline-flex items-center text-xs font-bold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200">
          📈 {roi}% ROI
        </span>
      );
    } else if (type === 'external') {
      const roi = item.expectedROI || item.expectedReturn;
      badges.push(
        <span key="ext" className="inline-flex items-center gap-1 text-xs font-bold text-purple-700 bg-gradient-to-r from-purple-100 to-pink-100 px-3 py-1.5 rounded-full border border-purple-200">
          🏢 EXTERNAL
        </span>
      );
      badges.push(
        <span key="ver" className="inline-flex items-center gap-1 text-xs font-bold text-indigo-700 bg-indigo-50 px-3 py-1.5 rounded-full border border-indigo-200">
          ✓ VERIFIED
        </span>
      );
      if (roi) badges.push(
        <span key="roi" className="inline-flex items-center text-xs font-bold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200">
          📈 {roi}% ROI
        </span>
      );
    }
    
    return badges;
  };

  // Render metrics based on type
  const renderMetrics = () => {
    if (type === 'creator') {
      return (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-3 rounded-xl text-center border border-slate-200 hover:shadow-md transition-shadow">
              <div className="text-xs font-semibold text-slate-500 mb-1">Followers</div>
              <div className="font-bold text-slate-900 text-lg">{formatNumber(item.totalFollowers)}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-xl text-center border border-blue-200 hover:shadow-md transition-shadow">
              <div className="text-xs font-semibold text-blue-600 mb-1">Avg Views</div>
              <div className="font-bold text-blue-900 text-lg">{formatNumber(item.avgViews || 0)}</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-xl text-center border border-green-200 hover:shadow-md transition-shadow">
              <div className="text-xs font-semibold text-green-600 mb-1">Growth</div>
              <div className="font-bold text-green-700 text-lg">+{item.growthRate || 0}%</div>
            </div>
          </div>
          <div className="mb-4 p-4 bg-gradient-to-br from-blue-50 via-blue-100 to-cyan-50 border-2 border-blue-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <div className="text-xs font-bold text-blue-600 mb-2 flex items-center gap-2">
              <span className="text-base">💰</span>
              Monthly Funding Needed
            </div>
            <div className="font-bold text-blue-700 text-2xl">{formatCurrency(item.monthlyFunding)}<span className="text-sm text-blue-600">/month</span></div>
          </div>
          <div className="mb-4 p-4 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 border-2 border-green-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-xl">🎯</span>
                <span className="text-xs font-bold text-green-700">Your Revenue Share</span>
              </div>
              <span className="font-bold text-green-700 text-xl">{item.revenueShare || 20}%</span>
            </div>
            <div className="text-xs text-green-600 mt-2 font-semibold">of all earnings monthly</div>
          </div>
        </>
      );
    }

    if (type === 'funding') {
      return (
        <>
          <div className="mb-4 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 shadow-sm">
            <div className="flex justify-between text-sm mb-3">
              <span className="font-bold text-purple-700 flex items-center gap-2">
                <span className="text-base">🎯</span>
                Funding Progress
              </span>
              <span className="font-bold text-purple-700 text-lg">{fundedPercent}%</span>
            </div>
            <div className="relative w-full bg-slate-200 rounded-full h-4 mb-3 overflow-hidden shadow-inner">
              <div 
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full transition-all duration-500 shadow-lg" 
                style={{ width: `${Math.min(fundedPercent, 100)}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full" />
              </div>
            </div>
            <div className="flex justify-between text-xs font-semibold">
              <span className="text-purple-700">{formatCurrency(item.currentFunding)} raised</span>
              <span className="text-purple-600">Goal: {formatCurrency(item.fundingGoal)}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-4 rounded-xl border-2 border-green-200 hover:shadow-lg transition-shadow">
              <div className="text-xs font-bold text-green-600 mb-2 flex items-center gap-1">
                <span>📈</span> Expected ROI
              </div>
              <div className="font-bold text-green-700 text-2xl">{item.expectedROI || item.expectedReturn || 15}%</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-100 p-4 rounded-xl border-2 border-blue-200 hover:shadow-lg transition-shadow">
              <div className="text-xs font-bold text-blue-600 mb-2 flex items-center gap-1">
                <span>💵</span> Min Amount
              </div>
              <div className="font-bold text-blue-700 text-xl">{formatCurrency(item.minInvestment)}</div>
            </div>
          </div>
          <div className="mb-4 p-4 bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-orange-700 flex items-center gap-1">
                <span>👥</span> {item.investorCount || 0} backers
              </span>
              {daysLeft !== null && daysLeft <= 30 && (
                <span className="text-orange-700 font-bold text-sm flex items-center gap-1 animate-pulse">
                  ⏰ {daysLeft} days left
                </span>
              )}
            </div>
            <div className="text-xs font-bold text-red-600 flex items-center gap-1">
              🔥 {item.last24HoursInvestments || Math.floor(Math.random() * 20)} invested in last 24h
            </div>
          </div>
        </>
      );
    }

    if (type === 'network' || type === 'external') {
      return (
        <>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-4 rounded-xl border-2 border-green-200 hover:shadow-lg transition-shadow">
              <div className="text-xs font-bold text-green-600 mb-2 flex items-center gap-1">
                <span>💰</span> Monthly Revenue
              </div>
              <div className="font-bold text-green-700 text-xl">{formatCurrency(item.monthlyRevenue)}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border-2 border-blue-200 hover:shadow-lg transition-shadow">
              <div className="text-xs font-bold text-blue-600 mb-2 flex items-center gap-1">
                <span>📈</span> Expected ROI
              </div>
              <div className="font-bold text-blue-700 text-xl">{item.expectedROI || item.expectedReturn || 15}%<span className="text-sm">/yr</span></div>
            </div>
          </div>
          <div className="mb-4 p-4 bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 border-2 border-purple-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold text-purple-700 flex items-center gap-1">
                <span>💵</span> Investment Range
              </span>
              <span className="text-sm font-bold text-purple-800">
                {formatCurrency(item.minInvestment)} - {formatCurrency(item.maxInvestment)}
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-purple-200">
              <span className="text-xs font-semibold text-purple-600 flex items-center gap-1">
                <span>👥</span> {item.investorCount || 0} investors
              </span>
              <span className="text-xs font-semibold text-purple-600 flex items-center gap-1">
                <span>💰</span> {formatCurrency(item.totalInvested)} invested
              </span>
            </div>
          </div>
          <div className="mb-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 flex items-center gap-3">
            <div className="flex -space-x-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 border-3 border-white shadow-md" />
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 border-3 border-white shadow-md" />
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 border-3 border-white shadow-md" />
            </div>
            <span className="text-xs font-bold text-green-700 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              {item.last7DaysInvestments || Math.floor(Math.random() * 15)} invested this week
            </span>
          </div>
        </>
      );
    }
  };

  const title = type === 'creator' ? item.creatorName : item.title;
  const description = item.description || (type === 'creator' ? `${item.category} creator` : 'Investment opportunity');

  return (
    <article 
      className="group relative bg-white border-2 border-slate-200 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl hover:border-purple-300 transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      onClick={handleViewDetails}
    >
      {/* Decorative top border */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${getGradientColors()}`} />
      
      {/* Image Section with Enhanced Gradient */}
      <div className={`relative w-full h-52 bg-gradient-to-br ${getGradientColors()} flex items-center justify-center overflow-hidden`}>
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-pulse" />
        </div>
        
        {/* Icon */}
        <span className="text-7xl drop-shadow-2xl transform group-hover:scale-110 transition-transform duration-300">
          {getIcon()}
        </span>
        
        {/* Status Badge */}
        <div className="absolute top-4 right-4">
          <span className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-xs font-bold bg-white text-green-600 shadow-lg border-2 border-green-200">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            {item.status === 'GENERATING_REVENUE' ? 'ACTIVE' : 'FUNDING'}
          </span>
        </div>
        
        {/* Activity Indicator */}
        {item.last24HoursInvestments > 0 && (
          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-md px-4 py-2 rounded-full text-xs font-bold flex items-center gap-2 shadow-lg border border-white/20">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
            </span>
            <span className="text-slate-700">{item.last24HoursInvestments} today</span>
          </div>
        )}
        
        {/* Trending indicator */}
        {(item.isFeatured || item.trending) && (
          <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg animate-pulse">
            🔥 TRENDING
          </div>
        )}
      </div>
      
      {/* Content Section */}
      <div className="p-6">
        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {renderBadges()}
        </div>
        
        {/* Title */}
        <h3 className="font-bold text-xl text-slate-900 group-hover:text-purple-600 transition-colors mb-3 line-clamp-2">
          {title}
        </h3>
        
        {/* Description */}
        <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-3">
          {description}
        </p>
        
        {/* Metrics */}
        {renderMetrics()}
        
        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button 
            onClick={handleViewDetails}
            className="flex-1 px-5 py-3 rounded-xl font-bold text-slate-700 bg-gradient-to-br from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 border-2 border-slate-300 transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
          >
            View Details
          </button>
          <button 
            onClick={handleInvest}
            className={`flex-1 px-5 py-3 rounded-xl font-bold text-white bg-gradient-to-r ${getGradientColors()} hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 hover:scale-105 relative overflow-hidden group/btn`}
          >
            {/* Button shine effect */}
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover/btn:opacity-30 group-hover/btn:animate-pulse" />
            <span className="relative">Invest Now</span>
          </button>
        </div>
      </div>
      
      {/* Corner decoration */}
      <div className={`absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-br ${getGradientColors()} rounded-full opacity-5 group-hover:opacity-10 transition-opacity`} />
    </article>
  );
};

export default InvestmentCard;